/**
 * Video Effects Utility Functions
 * Provides utilities for calculating effect progress, easing, and CSS conversion
 */

/**
 * Calculate effect progress based on current time
 * @param {number} currentTime - Current time in seconds
 * @param {number} effectStart - Effect start time in seconds
 * @param {number} effectDuration - Effect duration in seconds
 * @returns {number} Progress value from 0 to 1, or null if effect is not active
 */
export function calculateEffectProgress(currentTime, effectStart, effectDuration) {
  // Before effect starts
  if (currentTime < effectStart) {
    return null; // Effect not active yet
  }
  
  // After effect ends - return 1.0 to maintain final state
  if (currentTime >= effectStart + effectDuration) {
    return 1.0; // Maintain final state
  }
  
  // During effect - calculate progress (0 to 1)
  const progress = (currentTime - effectStart) / effectDuration;
  return Math.max(0, Math.min(1, progress)); // Clamp to 0-1
}

/**
 * Apply easing function to progress value
 * @param {number} progress - Progress value (0 to 1)
 * @param {string} easing - Easing type: 'linear', 'easeIn', 'easeOut', 'easeInOut'
 * @returns {number} Eased progress value
 */
export function applyEasing(progress, easing = 'linear') {
  switch (easing) {
    case 'easeIn':
      return progress * progress;
    case 'easeOut':
      return 1 - (1 - progress) * (1 - progress);
    case 'easeInOut':
      if (progress < 0.5) {
        return 2 * progress * progress;
      } else {
        return 1 - 2 * (1 - progress) * (1 - progress);
      }
    case 'bounce':
      if (progress < 1 / 2.75) {
        return 7.5625 * progress * progress;
      } else if (progress < 2 / 2.75) {
        progress -= 1.5 / 2.75;
        return 7.5625 * progress * progress + 0.75;
      } else if (progress < 2.5 / 2.75) {
        progress -= 2.25 / 2.75;
        return 7.5625 * progress * progress + 0.9375;
      } else {
        progress -= 2.625 / 2.75;
        return 7.5625 * progress * progress + 0.984375;
      }
    default: // linear
      return progress;
  }
}

/**
 * Convert Adjust effect parameters to CSS filter string
 * @param {Object} effect - Effect object with adjust parameters
 * @param {number} progress - Effect progress (0 to 1)
 * @returns {string} CSS filter string
 */
export function adjustEffectToCSS(effect, progress) {
  const filters = [];
  
  // Brightness: -100 to +100, convert to CSS brightness (0% to 200%)
  if (effect.brightness !== undefined) {
    const brightness = 1 + (effect.brightness / 100) * progress;
    filters.push(`brightness(${brightness})`);
  }
  
  // Contrast: -100 to +100, convert to CSS contrast (0% to 200%)
  if (effect.contrast !== undefined) {
    const contrast = 1 + (effect.contrast / 100) * progress;
    filters.push(`contrast(${contrast})`);
  }
  
  // Saturation: -100 to +100, convert to CSS saturate (0% to 200%)
  if (effect.saturation !== undefined) {
    const saturation = 1 + (effect.saturation / 100) * progress;
    filters.push(`saturate(${saturation})`);
  }
  
  // Exposure: -5 to +5 stops, convert to brightness multiplier
  if (effect.exposure !== undefined) {
    const exposure = Math.pow(2, effect.exposure * progress);
    filters.push(`brightness(${exposure})`);
  }
  
  // Highlights, Shadows, Whites, Blacks - these require more complex processing
  // For CSS, we can approximate with brightness/contrast adjustments
  // Full implementation would require canvas manipulation
  
  return filters.join(' ');
}

/**
 * Convert Transform effect parameters to CSS transform string
 * @param {Object} effect - Effect object with transform parameters
 * @param {number} progress - Effect progress (0 to 1)
 * @param {Object} baseTransform - Base transform values (from item properties)
 * @returns {string} CSS transform string
 * 
 * IMPORTANT: Position values (positionX, positionY) are calculated relative to the PAGE,
 * not relative to any container box (e.g., PowerClip). The whole item should behave
 * properly regardless of whether it's inside a container. All calculations and positioning
 * are based on the page coordinate system.
 */
export function transformEffectToCSS(effect, progress, baseTransform = {}) {
  const transforms = [];
  
  // Check if effect uses from/to interpolation
  const hasFromTo = effect.from && effect.to;
  
  if (hasFromTo) {
    // Interpolate from 'from' to 'to' values
    const from = effect.from;
    const to = effect.to;
    
    // Position interpolation
    // NOTE: Position values are relative to the PAGE, not any container box (e.g., PowerClip)
    // The whole item should behave properly regardless of whether it's inside a container
    if ((from.positionX !== undefined || to.positionX !== undefined) || 
        (from.positionY !== undefined || to.positionY !== undefined)) {
      const fromX = from.positionX || 0;
      const fromY = from.positionY || 0;
      const toX = to.positionX || 0;
      const toY = to.positionY || 0;
      const x = fromX + (toX - fromX) * progress;
      const y = fromY + (toY - fromY) * progress;
      transforms.push(`translate(${x}px, ${y}px)`);
    }
    
    // Scale interpolation
    if ((from.scaleX !== undefined || to.scaleX !== undefined) || 
        (from.scaleY !== undefined || to.scaleY !== undefined)) {
      const fromScaleX = from.scaleX !== undefined ? from.scaleX : 1;
      const fromScaleY = from.scaleY !== undefined ? from.scaleY : 1;
      const toScaleX = to.scaleX !== undefined ? to.scaleX : 1;
      const toScaleY = to.scaleY !== undefined ? to.scaleY : 1;
      const scaleX = fromScaleX + (toScaleX - fromScaleX) * progress;
      const scaleY = fromScaleY + (toScaleY - fromScaleY) * progress;
      transforms.push(`scale(${scaleX}, ${scaleY})`);
    }
    
    // Rotation interpolation
    if (from.rotation !== undefined || to.rotation !== undefined) {
      const fromRotation = from.rotation || 0;
      const toRotation = to.rotation || 0;
      const rotation = fromRotation + (toRotation - fromRotation) * progress;
      transforms.push(`rotate(${rotation}deg)`);
    }
  } else {
    // Fallback to absolute values (current behavior)
    // Position
    if (effect.positionX !== undefined || effect.positionY !== undefined) {
      const x = (effect.positionX || 0) * progress;
      const y = (effect.positionY || 0) * progress;
      transforms.push(`translate(${x}px, ${y}px)`);
    }
    
    // Scale
    if (effect.scaleX !== undefined || effect.scaleY !== undefined) {
      const scaleX = effect.scaleX !== undefined ? effect.scaleX : 1;
      const scaleY = effect.scaleY !== undefined ? effect.scaleY : 1;
      transforms.push(`scale(${scaleX}, ${scaleY})`);
    }
    
    // Rotation
    if (effect.rotation !== undefined) {
      const rotation = effect.rotation * progress;
      transforms.push(`rotate(${rotation}deg)`);
    }
  }
  
  // Anchor point - set transform origin
  if (effect.anchorPointX !== undefined || effect.anchorPointY !== undefined) {
    const anchorX = (effect.anchorPointX || 0.5) * 100;
    const anchorY = (effect.anchorPointY || 0.5) * 100;
    // Note: transform-origin is a separate CSS property, not part of transform
    // This would need to be applied separately
  }
  
  return transforms.join(' ');
}

/**
 * Convert Blur effect parameters to CSS filter string
 * @param {Object} effect - Effect object with blur parameters
 * @param {number} progress - Effect progress (0 to 1)
 * @returns {string} CSS filter string
 */
export function blurEffectToCSS(effect, progress) {
  const blurType = effect.blurType || 'gaussian';
  const blurRadius = (effect.blurRadius || 0) * progress;
  
  switch (blurType) {
    case 'gaussian':
    case 'motion':
      // CSS blur approximates Gaussian blur
      return `blur(${blurRadius}px)`;
    case 'directional':
      // Directional blur requires canvas manipulation, CSS can approximate with motion blur
      return `blur(${blurRadius}px)`;
    case 'radial':
      // Radial blur requires canvas manipulation, CSS can approximate with Gaussian blur
      return `blur(${blurRadius}px)`;
    default:
      return `blur(${blurRadius}px)`;
  }
}

/**
 * Apply opacity for dissolve/transition effects
 * @param {Object} effect - Effect object
 * @param {number} progress - Effect progress (0 to 1)
 * @returns {number} Opacity value (0 to 1)
 */
export function dissolveEffectToOpacity(effect, progress) {
  const fadeIn = effect.fadeIn || 0;
  const fadeOut = effect.fadeOut || 0;
  const duration = effect.duration || 1;
  
  // Calculate fade in progress
  if (progress < fadeIn / duration) {
    return progress / (fadeIn / duration);
  }
  
  // Calculate fade out progress
  if (progress > 1 - (fadeOut / duration)) {
    const fadeOutProgress = (1 - progress) / (fadeOut / duration);
    return fadeOutProgress;
  }
  
  // Fully visible
  return 1;
}

/**
 * Apply opacity for transition effects
 * @param {Object} effect - Effect object with transition parameters
 * @param {number} progress - Effect progress (0 to 1)
 * @returns {number} Opacity value (0 to 1)
 */
export function transitionEffectToOpacity(effect, progress) {
  const transitionType = effect.transitionType || 'crossfade';
  const direction = effect.direction || 'left';
  
  switch (transitionType) {
    case 'crossfade':
      // Simple fade in (0 to 1)
      return progress;
      
    case 'wipe':
      // Wipe transition - reveal from direction
      // For single item, this acts as a reveal effect
      return progress;
      
    case 'slide':
      // Slide transition - similar to wipe but with movement
      return progress;
      
    case 'zoom':
      // Zoom transition - fade in with scale
      return progress;
      
    default:
      return progress;
  }
}

/**
 * Stack multiple effects and combine their CSS outputs
 * @param {Array} effects - Array of effect objects
 * @param {number} currentTime - Current time in seconds
 * @returns {Object} Combined CSS properties { filter, transform, opacity, transformOrigin }
 */
export function stackEffects(effects, currentTime) {
  const result = {
    filter: '',
    transform: '',
    opacity: 1,
    transformOrigin: '50% 50%'
  };
  
  const filters = [];
  const transforms = [];
  let opacity = 1;
  
  effects.forEach(effect => {
    if (!effect.enabled) return;
    
    const progress = calculateEffectProgress(
      currentTime,
      effect.start || 0,
      effect.duration || 1
    );
    
    // Skip if effect hasn't started yet (progress === null)
    if (progress === null) return;
    
    // When progress === 1.0, effect has ended - maintain final state
    // Apply easing only during active animation (0 to 1), not after it ends
    const easedProgress = progress >= 1.0 ? 1.0 : applyEasing(progress, effect.easing || 'linear');
    
    switch (effect.type) {
      case 'adjust':
        const adjustFilter = adjustEffectToCSS(effect, easedProgress);
        if (adjustFilter) filters.push(adjustFilter);
        break;
        
      case 'transform':
        const transformCSS = transformEffectToCSS(effect, easedProgress);
        if (transformCSS) transforms.push(transformCSS);
        
        // Handle opacity - support from/to or direct value
        if (effect.from && effect.to) {
          // Interpolate opacity from 'from' to 'to'
          const fromOpacity = (effect.from.opacity !== undefined ? effect.from.opacity : 100) / 100;
          const toOpacity = (effect.to.opacity !== undefined ? effect.to.opacity : 100) / 100;
          const interpolatedOpacity = fromOpacity + (toOpacity - fromOpacity) * easedProgress;
          opacity *= interpolatedOpacity;
        } else if (effect.opacity !== undefined) {
          // Direct opacity value
          opacity *= (effect.opacity / 100) * easedProgress + (1 - easedProgress);
        }
        
        if (effect.anchorPointX !== undefined || effect.anchorPointY !== undefined) {
          const anchorX = (effect.anchorPointX || 0.5) * 100;
          const anchorY = (effect.anchorPointY || 0.5) * 100;
          result.transformOrigin = `${anchorX}% ${anchorY}%`;
        }
        break;
        
      case 'blur':
        const blurFilter = blurEffectToCSS(effect, easedProgress);
        if (blurFilter) filters.push(blurFilter);
        break;
        
      case 'dissolve':
        opacity *= dissolveEffectToOpacity(effect, easedProgress);
        break;
        
      case 'transition':
        opacity *= transitionEffectToOpacity(effect, easedProgress);
        break;
        
      case 'fade':
        if (effect.fadeType === 'in') {
          opacity *= easedProgress;
        } else if (effect.fadeType === 'out') {
          opacity *= (1 - easedProgress);
        } else if (effect.fadeType === 'inout') {
          if (easedProgress < 0.5) {
            opacity *= easedProgress * 2;
          } else {
            opacity *= (1 - easedProgress) * 2;
          }
        }
        break;
    }
  });
  
  result.filter = filters.join(' ');
  result.transform = transforms.join(' ');
  result.opacity = Math.max(0, Math.min(1, opacity));
  
  return result;
}

/**
 * Get active effects at a given time
 * @param {Array} effects - Array of effect objects
 * @param {number} currentTime - Current time in seconds
 * @returns {Array} Array of active effect objects
 */
export function getActiveEffects(effects, currentTime) {
  if (!effects || !Array.isArray(effects)) return [];
  
  return effects.filter(effect => {
    if (!effect.enabled) return false;
    
    const start = effect.start || 0;
    const duration = effect.duration || 1;
    return currentTime >= start && currentTime <= start + duration;
  });
}

